import {zodResolver} from '@hookform/resolvers/zod';
import {Checkbox, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {useLocalStorage} from '@mantine/hooks';
import type {ChangeEvent} from 'react';
import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {AuthenticationTypeEnum} from 'src/api/ProactApiClient';
import {
  configureAmplify,
  writeAuthType,
  writeSsoClientId,
} from 'src/app/security/authentication/configureAmplify';
import {Button} from 'src/design/atoms/Button';
import {useLazyCheckUserStatusQuery} from 'src/redux/queries/user';
import {useAppDispatch} from 'src/redux/hooks';
import {
  setIdentityProvider,
  setIsDomainChecked,
  setUserAccountStatus,
  setUserMfaEnabled,
  setUsername,
} from 'src/redux/slices/authSlice';
import {z} from 'zod';

const schema = z.object({
  username: z
    .string()
    .email('Please enter a valid email address.')
    .min(1, 'Email is required.'),
});

type FormValues = z.infer<typeof schema>;

export const DomainCheck: React.FC = () => {
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const isLoading = isLocalLoading;

  const [error, setError] = useState<string | null>(null);

  const [rememberMe, setRememberMe] = useLocalStorage<boolean>({
    key: 'remember-me',
    defaultValue: false,
  });
  const [rememberMeUser, setRememberMeUser] = useLocalStorage<string>({
    key: 'remember-me-user',
    defaultValue: '',
  });

  const dispatch = useAppDispatch();
  const [checkUserStatus] = useLazyCheckUserStatusQuery();

  const {
    control,
    handleSubmit: handleFormSubmit,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      username: rememberMeUser,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    setValue('username', rememberMeUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rememberMeUser]);

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLocalLoading(true);
      setError(null);

      if (rememberMe == true) {
        setRememberMeUser(values.username);
      }

      const response = await checkUserStatus({
        email: values.username,
      }).unwrap();

      dispatch(setUserAccountStatus(response.status));
      dispatch(setUserMfaEnabled(response.mfaEnabled));

      writeAuthType(response.domainAuth.authType);

      if (response.domainAuth.authType == AuthenticationTypeEnum.SSO) {
        const appClientId = response.domainAuth.appClientId;
        writeSsoClientId(appClientId);
        configureAmplify(appClientId ?? undefined);

        dispatch(setIdentityProvider(response.domainAuth.providerName!));
      } else {
        // Drop any client id left by an earlier SSO attempt.
        writeSsoClientId(null);
        configureAmplify();

        dispatch(setUsername(values.username));
      }

      dispatch(setIsDomainChecked(true));
    } catch (error) {
      console.error('Domain check error:', error);
      setError(
        'An error occurred while verifying your email address. Please check your connection and try again.'
      );
    } finally {
      setIsLocalLoading(false);
    }
  };

  function handleRememberMeChange(event: ChangeEvent<HTMLInputElement>): void {
    setRememberMe(event.currentTarget.checked);

    if (event.currentTarget.checked === false) {
      setRememberMeUser('');
    }
  }

  return (
    <>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <Controller
          name="username"
          control={control}
          render={({field, fieldState}) => (
            <TextInput
              label="Email"
              placeholder="me@example.com"
              {...field}
              error={fieldState.error?.message}
              autoComplete="email"
              autoFocus
            />
          )}
        />
        <Group justify="space-between" mt="lg">
          <Checkbox
            label="Remember me"
            checked={rememberMe}
            onChange={handleRememberMeChange}
          />
        </Group>

        <Button
          fullWidth
          mt="xl"
          type="submit"
          disabled={isLoading}
          loading={isLoading}>
          Continue
        </Button>

        {error && (
          <Alert mt="md" color="red">
            {error}
          </Alert>
        )}
      </form>
    </>
  );
};
